import { animate, state, style, transition, trigger } from '@angular/animations';
import { Clipboard } from '@angular/cdk/clipboard';
import { Component, HostListener, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { MatDialogRef } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { LoaderService } from '@app/core/loader/loader.service';
import { NotificationService } from '@app/core/notifications/notification.service';
import { IfIsNotAuthorized } from '@app/shared/autorisations/autorisations.decorator.ts';
import { Traitement } from '@app/shared/autorisations/traitements';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { DownloadService } from '@app/shared/download/download.service';
import { StructureService } from '@app/shared/structure/structure.service';
import {
    Espace,
    ExporterResponse,
    MaquettesService,
    NomenclatureErreurValidation,
    NomenclaturesErreur,
    NomenclaturesService,
    ObjetMaquetteDetail,
    TypeEspace
} from '@pcscol/odf-api-v1-angular';
import { PegaseDialogType, MessageContextuelComponent } from '@pcscol-portail/composants-pegase';
import { Observable, Subject, of } from 'rxjs';
import { first, takeUntil, tap } from 'rxjs/operators';
import { CommandHistoryService } from '../../services/command-history.service';
import { EspaceService } from '../../services/espace.service';
import { TreeViewService } from '../../services/tree-view.service';
import {
    RecupererFormationsPorteusesResponseAgregeePourAffichage,
    ResultatsRecuperationFormationsPorteusesDialogComponent
} from '../resultats-recuperation-formations-porteuses-dialog/resultats-recuperation-formations-porteuses-dialog.component';
import { MatCard, MatCardContent, MatCardTitle, MatCardActions } from '@angular/material/card';
import { MatMiniFabButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { EnableIfAutoriseDirective } from '../../../../shared/autorisations/directives/enable-if-autorise.directive';
import { MatFormField, MatLabel, MatInput, MatSuffix, MatHint } from '@angular/material/input';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatDivider } from '@angular/material/list';
import { EspaceChipComponent } from '../../../../shared/base-form/espace-chip/espace-chip.component';
import { EspaceSelectorComponent } from '../../../../shared/espace-selector/espace-selector.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatRowDef,
    MatRow,
    MatHeaderRowDef,
    MatHeaderRow
} from '@angular/material/table';
import { NgClass, AsyncPipe } from '@angular/common';

interface RecupererFormationsPorteusesResponseEnObjet {
    codeObjet: string;
    codeFormationPorteuseDeCharge: string;
    succes: boolean;
    message: string;
}

@Component({
    selector: 'app-toolbox',
    templateUrl: './toolbox.component.html',
    styleUrls: ['./toolbox.component.scss'],
    animations: [
        trigger('detailExpand', [
            state(
                'collapsed',
                style({
                    height: '0px',
                    minHeight: '0',
                    visibility: 'hidden',
                    opacity: 0.6
                })
            ),
            state(
                'expanded',
                style({
                    height: '*',
                    visibility: 'visible',
                    opacity: 1
                })
            ),
            transition('expanded <=> collapsed', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
        ])
    ],
    imports: [
        MatCard,
        MatCardContent,
        MatMiniFabButton,
        MatTooltip,
        MatIcon,
        EnableIfAutoriseDirective,
        MatCheckbox,
        MatFormField,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        MatSuffix,
        MatHint,
        MatButton,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        MatDivider,
        MessageContextuelComponent,
        MatCardTitle,
        EspaceChipComponent,
        EspaceSelectorComponent,
        MatProgressSpinner,
        RouterLink,
        MatTable,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        MatRowDef,
        MatRow,
        NgClass,
        MatHeaderRowDef,
        MatHeaderRow,
        MatCardActions,
        AsyncPipe
    ]
})
export class ToolboxComponent implements OnDestroy, OnInit {
    history = inject(CommandHistoryService);
    private router = inject(Router);
    private structureService = inject(StructureService);
    private treeView = inject(TreeViewService);
    private espaceService = inject(EspaceService);
    private maquetteService = inject(MaquettesService);
    private nomenclaturesService = inject(NomenclaturesService);
    private fb = inject(NonNullableFormBuilder);
    private notificationService = inject(NotificationService);
    private clipBoard = inject(Clipboard);
    private dialogService = inject(DialogService);
    private loadingService = inject(LoaderService);
    private downloadService = inject(DownloadService);

    readonly ODF_CREER_OBJET_MAQUETTE = Traitement.ODF_CREER_OBJET_MAQUETTE;
    readonly ODF_COPIER_MAQUETTE_EXISTANT_VERS_ESPACE = Traitement.ODF_COPIER_MAQUETTE_EXISTANT_VERS_ESPACE;
    readonly ODF_EXPORTER_MAQUETTE = Traitement.ODF_EXPORTER_MAQUETTE;
    TypeEspace = TypeEspace;
    @HostListener('window:keydown.control.p', ['$event'])
    new($event) {
        $event.preventDefault();
        if (this.espaceService.espaceSelected?.id) {
            this.onCreerObjetMaquette();
        }
    }

    @ViewChild('importerDialogTemplate') importerDialogTemplate: TemplateRef<unknown>;
    @ViewChild('nomenclaturesErreursTpl') nomenclaturesErreursTpl: TemplateRef<unknown>;
    @ViewChild('importSuccessNotificationTemplate') importSuccessNotificationTemplate: TemplateRef<unknown>;
    @ViewChild('recupererFormationsPorteusesTemplate') recupererFormationsPorteusesTemplate: TemplateRef<unknown>;

    racine$ = this.treeView.racineMaquette$;
    historiqueRacine$ = this.treeView.maquetteHistory$;

    private destroyed$ = new Subject<boolean>();

    private _downloadService = inject(DownloadService);

    ngOnInit(): void {
        this.preventHistoriqueLoadingIfNotAutorized();
    }

    espaceActuel$: Observable<Espace | null> = this.espaceService.espaceSelected$;
    filtresPartiesArbre$ = this.treeView.filtresPartiesArbre$;
    rechercheActuelleResultsCount$ = this.treeView.rechercheActuelleCount$;
    loading$ = this.loadingService.loading$;
    tabsLayout = this.treeView.tabsLayout$;
    viewLayout$ = this.treeView.viewLayout$;
    sidebarOpened$ = this.treeView.sidebarOpened$;
    graphOrientation$ = this.treeView.graphOrientation$;

    textFilter = this.fb.control<string | null>(null);
    displayActionsAsIcons = this.fb.control(true);

    objetMaquetteImporte: ObjetMaquetteDetail;

    canUndo() {
        this.history.undo();
    }

    undo() {
        this.history.undo();
    }

    redo() {
        this.history.redo();
    }

    canOpenImportDialog(): boolean {
        return !!this.racine$.getValue();
    }

    canShare(): boolean {
        return !!this.racine$.getValue();
    }

    onCreerObjetMaquette() {
        this.router.navigate([
            this.structureService.current$.getValue(),
            'objets-maquette',
            'editeur',
            'creer',
            this.espaceService.espaceSelected?.id
        ]);
    }

    ngOnDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    doExpandTree() {
        this.treeView.expandTree(true);
    }

    doCollapseTree() {
        this.treeView.expandTree(false);
    }

    share() {
        if (this.racine$.getValue()?.id) {
            this.maquetteService
                .exporter(this.structureService.current$.getValue(), this.racine$.getValue()!.id!)
                .pipe(first(), takeUntil(this.destroyed$))
                .subscribe((response: ExporterResponse) => {
                    this.copy(response.base64Encode);
                    this.notificationService.success(`La maquette a bien été copiée dans le presse-papier.`);
                });
        }
    }

    public erreursNomenclatures: NomenclatureErreurValidation[] | null = null;
    expandedRows: Set<string> = new Set();

    /**
     * returns true if the contexts row of the object is opened
     */
    isExpandedRow(objet): boolean {
        return this.expandedRows.has(objet.codeObjetMaquette);
    }

    /**
     * returns true if the rows should be expandable
     */
    isOptionalRow(index: number, objet): boolean {
        if (objet.nomenclatureList?.length > 1) {
            return true;
        }
        return false;
    }

    toggleRowExpansion(element): void {
        if (element.codeObjetMaquette) {
            if (this.expandedRows.has(element.codeObjetMaquette)) {
                this.expandedRows.delete(element.codeObjetMaquette);
            } else {
                this.expandedRows.add(element.codeObjetMaquette);
            }
        }
    }

    verifierNomenclatures() {
        this.erreursNomenclatures = null;
        if (this.racine$.getValue()?.id) {
            this.nomenclaturesService
                .verifierNomenclatures(this.structureService.current$.getValue(), this.racine$.getValue()!.id!)
                .pipe(takeUntil(this.destroyed$))
                .subscribe((erreurs: NomenclaturesErreur) => {
                    if (!erreurs || !erreurs.nomenclaturesErreur) {
                        this.notificationService.success(`La maquette contient des données de nomenclatures à jour.`);
                    } else {
                        this.erreursNomenclatures = erreurs.nomenclaturesErreur;
                        const dialog = this.dialogService.openDialog({
                            titre: 'La maquette contient des nomenclatures invalides',
                            type: PegaseDialogType.WARN,
                            template: this.nomenclaturesErreursTpl
                        });
                        dialog
                            .afterClosed()
                            .pipe(takeUntil(this.destroyed$))
                            .subscribe(() => {
                                this.erreursNomenclatures = null;
                            });
                    }
                });
        }
    }

    downloadErreursNomenclaturesAsCSV(): void {
        const codeRacine = this.racine$.getValue()?.code;
        const codeEspace = this.espaceService.espaceSelected?.code;
        if (codeEspace && codeRacine) {
            this.downloadService.downloadErreursNomenclaturesAsCSV(this.erreursNomenclatures!, codeRacine, codeEspace);
        }
    }

    // Mandatory in case of big text
    copy(text: string) {
        const pending = this.clipBoard.beginCopy(text);
        let remainingAttempts = 10;
        const attempt = () => {
            const result = pending.copy();
            if (!result && --remainingAttempts) {
                setTimeout(attempt);
            } else {
                // Remember to destroy when you're done!
                pending.destroy();
            }
        };
        attempt();
    }

    changeFiltrePartieArbre(event: MatCheckboxChange) {
        if (event.source.value === 'partieHaute') {
            this.treeView.togglePartieHaute();
        } else if (event.source.value === 'partieBasse') {
            this.treeView.togglePartieBasse();
        }
    }

    changeFiltrePartieArbreDirect(value: 'partieHaute' | 'partieBasse') {
        if (value === 'partieHaute') {
            this.treeView.togglePartieHaute();
        } else if (value === 'partieBasse') {
            this.treeView.togglePartieBasse();
        }
    }

    filtrerParTexte() {
        this.treeView.expandTree(true); //TODO check
        this.treeView.lancerRechercheTexte(this.textFilter.value);
    }

    resetText() {
        this.textFilter.setValue(null);
        this.filtrerParTexte();
    }

    toggleTabsLayout() {
        this.treeView.toggleTabsLayout();
    }
    toggleViewLayout() {
        this.treeView.toggleViewLayout();
    }
    toggleSideBar() {
        this.treeView.toggleSidebar();
    }
    toggleGraphOrientation() {
        this.treeView.toggleGraphOrientation();
    }

    /**
     * Copier vers un espace
     */
    copierVersForm = this.fb.group({
        espace: this.fb.control<Espace | null>(null, {
            validators: [
                Validators.required,
                (control: AbstractControl) => {
                    return control.value === this.espaceService.espaceSelected
                        ? { invalid: { message: 'Vous devez sélectionner une période différente' } }
                        : null;
                }
            ]
        })
    });

    private importDialogRef: MatDialogRef<unknown>;

    openImportDialog() {
        this.importDialogRef = this.dialogService.openDialog({
            titre: 'Copier vers un autre espace de travail',
            type: PegaseDialogType.INFO,
            template: this.importerDialogTemplate
        });
    }

    patchCopierMaquetteVersEspaceForm(event: Espace) {
        this.copierVersForm.controls.espace.patchValue(event);
    }
    submitCopierVersEspace() {
        if (this.copierVersForm.valid && this.racine$.getValue()?.id && this.copierVersForm.controls.espace.value?.id) {
            this.maquetteService
                .importerExistantVersEspace(this.structureService.current$.value, this.racine$.getValue()!.id!, {
                    espaceId: this.copierVersForm.controls.espace.value.id!
                })
                .pipe(
                    first(),
                    takeUntil(this.destroyed$),
                    tap(() => {
                        this.importDialogRef.close();
                    })
                )
                .subscribe((objetMaquette: ObjetMaquetteDetail) => {
                    this.objetMaquetteImporte = objetMaquette;
                    this.notificationService.successWithTemplate(this.importSuccessNotificationTemplate);
                });
        }
    }

    @IfIsNotAuthorized(Traitement.ODF_LIRE_HISTORIQUE)
    preventHistoriqueLoadingIfNotAutorized() {
        this.historiqueRacine$ = of(null);
    }

    /**
     * Copie des formations porteuses
     */

    private _recupererFormationsPorteusesDialog: MatDialogRef<unknown>;

    closeRecupererFormationsPorteusesDialog() {
        this._recupererFormationsPorteusesDialog.close();
    }

    recupererFormationsPorteusesForm = this.fb.group({
        espace: this.fb.control<Espace | null>(null, {
            validators: [
                Validators.required,
                (control: AbstractControl) => {
                    return control.value?.id === this.espaceService.espaceSelected?.id
                        ? { invalid: { message: 'Vous devez sélectionner une période différente' } }
                        : null;
                }
            ]
        })
    });

    patchRecupererFormationsPorteusesForm(event: Espace) {
        this.recupererFormationsPorteusesForm.controls.espace.patchValue(event);
    }

    openRecupererFormationsPorteusesDialog() {
        this._recupererFormationsPorteusesDialog = this.dialogService.openDialog({
            titre: 'Récupérer les formations porteuses depuis un espace source',
            type: PegaseDialogType.INFO,
            template: this.recupererFormationsPorteusesTemplate,
            dialogConfig: {
                maxWidth: '80%'
            }
        });
    }

    resultatsRecuperationFormationsPorteusesDeCharge?: RecupererFormationsPorteusesResponseAgregeePourAffichage[];

    responseCSVRecuperationFormationsPorteuses?: Blob;

    submitRecupererFormationsPorteuses() {
        if (
            this.recupererFormationsPorteusesForm.valid &&
            this.racine$.getValue()?.id &&
            this.recupererFormationsPorteusesForm.controls.espace.value?.id
        ) {
            this.responseCSVRecuperationFormationsPorteuses = undefined;
            this.maquetteService
                .appliquerFormationsPorteuses(this.structureService.current$.value, this.racine$.getValue()!.id!, {
                    espaceId: this.recupererFormationsPorteusesForm.controls.espace.value!.id!
                })
                .pipe(first(), takeUntil(this.destroyed$))
                .subscribe(async (response: Blob) => {
                    this.responseCSVRecuperationFormationsPorteuses = response;
                    this._recupererFormationsPorteusesDialog.afterClosed().subscribe(async () => {
                        this.resultatsRecuperationFormationsPorteusesDeCharge = await this.recuperationFormationsPorteusesformatCSVRecuVersReponseAgregee(
                            response
                        );
                        this.treeView.refresh();
                        this.openFormationsPorteusesErrorsDialog(this.resultatsRecuperationFormationsPorteusesDeCharge!);
                    });
                    this._recupererFormationsPorteusesDialog.close();
                });
        }
    }

    /**
     * pure
     */
    private async recuperationFormationsPorteusesformatCSVRecuVersReponseAgregee(
        csvBlob: Blob
    ): Promise<RecupererFormationsPorteusesResponseAgregeePourAffichage[]> {
        const csvAsText = await csvBlob.text();
        // On split en lignes et on vire les lignes vides
        const lines: string[] = csvAsText.split('\n').filter(s => s.length);
        // On ignore la ligne d'en-tête
        lines.splice(0, 1);

        const jsonBrut: RecupererFormationsPorteusesResponseEnObjet[] = lines.map((line: string) => {
            // On sépare chaque colonne et on ôte les guillements
            const temp = line.split(`;`).map(col => col.slice(1, -1));

            return {
                codeObjet: temp[0],
                codeFormationPorteuseDeCharge: temp[1],
                succes: temp[2] === 'SUCCES',
                message: temp[3]
            };
        });

        return jsonBrut.reduce((acc: RecupererFormationsPorteusesResponseAgregeePourAffichage[], val) => {
            const { codeFormationPorteuseDeCharge, codeObjet, message, succes } = val;
            const foundIndex = acc.findIndex(
                ligne => ligne.codeFormationPorteuseDeCharge === val.codeFormationPorteuseDeCharge && ligne.succes === val.succes
            );
            if (foundIndex != -1) {
                acc[foundIndex].objetsConcernes.push({ codeObjet, message });
            } else {
                acc.push({ codeFormationPorteuseDeCharge, succes, objetsConcernes: [{ codeObjet, message }] });
            }

            return acc;
        }, [] as RecupererFormationsPorteusesResponseAgregeePourAffichage[]);
    }

    openFormationsPorteusesErrorsDialog(data: RecupererFormationsPorteusesResponseAgregeePourAffichage[]) {
        const dialog = this.dialogService.openDialogWithComponent(ResultatsRecuperationFormationsPorteusesDialogComponent, {
            titre: data.some(x => !x.succes) ? 'Import avec des erreurs' : 'Import en succès',
            type: PegaseDialogType.INFO,
            dialogConfig: {
                data: {
                    resultats: data
                }
            },
            ...data,
            noCloseButton: false
        });
        dialog
            .afterClosed()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((shouldDownloadCSV: boolean) => {
                if (shouldDownloadCSV) {
                    this._downloadService.downloadCSVFromBlob(
                        this.responseCSVRecuperationFormationsPorteuses!,
                        `recuperation-formations-porteuses-resultat_${this.racine$.getValue()?.code}_depuis_${
                            this.recupererFormationsPorteusesForm.controls.espace.value?.code
                        }_${new Date().toLocaleDateString().replaceAll('/', '-')}.csv`
                    );
                }
            });
    }
}
